import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { ApiResponse } from '@/types'

export type AttendanceBoardDevice = {
  id: number
  device_sn: string
  name?: string | null
  attendance_mode?: string | null
  attendance_mode_label?: string | null
  active?: boolean
  last_seen_at?: string | null
}

export type AttendanceBoardLocation = {
  id: number
  place: string
  active?: boolean
  latitude?: string | null
  longitude?: string | null
  center_latlog?: string | null
  has_device: boolean
  device_sn?: string | null
  device_name?: string | null
  device?: AttendanceBoardDevice | null
}

export type AttendanceBoardRosterWorker = {
  worker_id: number
  worker_name: string
}

export type AttendanceBoardSegment = {
  segment_index?: number
  entry_id: number
  attendance_id?: number | null
  check_in: string | null
  check_out: string | null
  check_in_local?: string | null
  check_out_local?: string | null
  hours: string
  status: 'open' | 'closed' | 'missing' | 'expected' | string
  entry_status?: string | null
  source?: string | null
}

export type AttendanceBoardWorkerRow = AttendanceBoardSegment & {
  worker_id: number | null
  worker_name: string
  also_on_other_jobs?: boolean
  on_roster?: boolean
}

export type AttendanceBoardWorkerGroup = {
  worker_id: number | null
  worker_name: string
  also_on_other_jobs?: boolean
  segment_count: number
  total_hours: string
  segments: AttendanceBoardSegment[]
  on_roster?: boolean
}

export type AttendanceBoardDay = {
  /** Null for the branch-wide day (no location selected). */
  location: AttendanceBoardLocation | null
  date: string
  org_timezone?: string | null
  roster?: AttendanceBoardRosterWorker[]
  attendance_job: {
    id: number
    title: string
    job_ref: string
    job_code: string
    is_attendance_tracker: boolean
  } | null
  workers: AttendanceBoardWorkerRow[]
  worker_groups?: AttendanceBoardWorkerGroup[]
}

const unwrapPayload = <T>(payload?: ApiResponse<T> | T | null): T | null => {
  if (!payload) return null
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<T>).data ?? null
  }
  return payload as T
}

export const attendanceBoardApi = {
  listLocations: async (): Promise<AttendanceBoardLocation[]> => {
    const { data } = await api.get<ApiResponse<{ locations: AttendanceBoardLocation[] }>>(
      API_PATHS.workers.attendanceBoardLocations,
    )
    return unwrapPayload(data)?.locations ?? []
  },

  /** Without a location, returns every worker of the branch for the day. */
  getDay: async (locationId: number | string | null, date: string): Promise<AttendanceBoardDay | null> => {
    const params = new URLSearchParams()
    if (locationId != null) params.set('location_id', String(locationId))
    if (date) params.set('date', date)
    const { data } = await api.get<ApiResponse<AttendanceBoardDay>>(
      API_PATHS.workers.attendanceBoardDay,
      { params },
    )
    const payload = unwrapPayload(data)
    if (!payload || typeof payload !== 'object') return null
    const workers = Array.isArray(payload.workers) ? payload.workers : []
    const workerGroups = Array.isArray(payload.worker_groups) ? payload.worker_groups : []
    return {
      ...payload,
      workers,
      worker_groups: workerGroups.length > 0 ? workerGroups : groupWorkersByWorker(workers),
    }
  },

  listRoster: async (locationId: number | string): Promise<AttendanceBoardRosterWorker[]> => {
    const params = new URLSearchParams()
    params.set('location_id', String(locationId))
    const { data } = await api.get<ApiResponse<{ workers: AttendanceBoardRosterWorker[] }>>(
      API_PATHS.workers.attendanceBoardRoster,
      { params },
    )
    return unwrapPayload(data)?.workers ?? []
  },

  addRosterWorker: async (locationId: number | string, workerId: number | string): Promise<AttendanceBoardRosterWorker[]> => {
    const { data } = await api.post<ApiResponse<{ roster: AttendanceBoardRosterWorker[] }>>(
      API_PATHS.workers.attendanceBoardRoster,
      { location_id: Number(locationId), worker_id: Number(workerId) },
    )
    return unwrapPayload(data)?.roster ?? []
  },

  removeRosterWorker: async (locationId: number | string, workerId: number | string): Promise<AttendanceBoardRosterWorker[]> => {
    const params = new URLSearchParams()
    params.set('location_id', String(locationId))
    params.set('worker_id', String(workerId))
    const { data } = await api.delete<ApiResponse<{ workers: AttendanceBoardRosterWorker[] }>>(
      API_PATHS.workers.attendanceBoardRoster,
      { params },
    )
    return unwrapPayload(data)?.workers ?? []
  },
}

function groupWorkersByWorker(rows: AttendanceBoardWorkerRow[]): AttendanceBoardWorkerGroup[] {
  const grouped = new Map<string, AttendanceBoardWorkerGroup>()
  const order: string[] = []

  for (const row of rows) {
    const key = row.worker_id != null ? String(row.worker_id) : `name:${row.worker_name}`
    let group = grouped.get(key)
    if (!group) {
      group = {
        worker_id: row.worker_id,
        worker_name: row.worker_name,
        also_on_other_jobs: Boolean(row.also_on_other_jobs),
        segment_count: 0,
        total_hours: '0.00',
        segments: [],
      }
      grouped.set(key, group)
      order.push(key)
    }
    if (row.also_on_other_jobs) {
      group.also_on_other_jobs = true
    }
    group.segments.push({
      segment_index: group.segments.length + 1,
      entry_id: row.entry_id,
      attendance_id: row.attendance_id,
      check_in: row.check_in,
      check_out: row.check_out,
      check_in_local: row.check_in_local,
      check_out_local: row.check_out_local,
      hours: row.hours,
      status: row.status,
      entry_status: row.entry_status,
      source: row.source,
    })
  }

  return order.map((key) => {
    const group = grouped.get(key)!
    const total = group.segments.reduce((sum, segment) => sum + Number.parseFloat(segment.hours || '0'), 0)
    group.segment_count = group.segments.length
    group.total_hours = Number.isFinite(total) ? total.toFixed(2) : '0.00'
    return group
  })
}
